import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { EDITORIAL_CONTENT_NODE_ID } from '../../../constants';
import NavBar from './ui/nav_bar';
import HiddenNav from './ui/hidden_nav';
import UpNext from './up_next';
import IntroSlide from './intro_slide';
import BeginSlideshowButton from './ui/buttons/begin_slideshow';
import Section from '../../section/section';
import TextSection from '../../section/text_section';
import ProductDescriptions from '../products/product_descriptions';
import { scrollTo } from '../../../utils/dom';
import { scrollToTopDesktopAd } from '../../../utils/slideshow';
import { isHTMLSection } from '../../../utils/sections';
import {
  sendGASlideViewEvents,
  sendAnalyticsEvent,
  sendDataLayerSlideViewEvent,
  sendDataLayerEvent
} from '../../../utils/analytics';
import classNames from 'classnames';
import {get, omit, pick, trim, assign, isEmpty} from 'lodash';
import NativeAd from '../../common/native_ad';
import RightRailAd from '../right_rail_ad';
import Sections from '../../section/sections';
import RelatedEntries from '../related_entries';
import { defineMessages, injectIntl } from 'react-intl';
import { decorateIntlMessages } from '../../common/rosetta_formatted_message';
import TrinityAudioPlayer from '../../common/trinityAudioPlayer';

const i18nMessages = decorateIntlMessages(__filename, defineMessages({
  slide_number_alt_string: {
    id: 'slide_number_alt_string',
    description: 'Slide number used as image alt text (Slide 1)',
    defaultMessage: 'Slide {slideNumber}'
  }
}));


const AD_REFRESH_RATE_PER_SLIDE = 1;

export class DesktopLayout extends Component {
  state = {
    isHovering: false,
    hasHoveredOnLastSlide: false
  };

  componentDidMount() {
    this.props.updateUpNext();
    sendGASlideViewEvents(this.props.slideIndex);
    sendDataLayerSlideViewEvent(this.props.slideIndex);
    scrollTo(0, 0);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.slideIndex === this.props.slideIndex) {
      return;
    }
    sendGASlideViewEvents(this.props.slideIndex);
    sendDataLayerSlideViewEvent(this.props.slideIndex);
  }

  handleMouseEnter = () => this.setState({
    isHovering: true,
    hasHoveredOnLastSlide: this.onLastSlide()
  });

  handleMouseLeave = () => this.setState({
    isHovering: false,
    hasHoveredOnLastSlide: this.onLastSlide()
  });

  onIntroSlide = () =>
    this.props.slideIndex === 0;

  onLastSlide = () =>
    this.props.slideIndex === this.props.slideshowSections.length;

  shouldDisplayUpNextComponent = () =>
    !isEmpty(this.props.upNext) && this.onLastSlide();

  shouldRenderAsFullWidth = () =>
    this.props.isFullWidth
      || this.props.hasFullWidthIntroSlide && this.onIntroSlide();

  shouldShowInitialUpNextAnimation = () =>
    !(this.state.isHovering || this.state.hasHoveredOnLastSlide);

  introSlide = () => this.props.openerImage &&
    <IntroSlide handleClick={this.props.handleChangeSlide}
      openerImage={this.props.openerImage}
      socialIconProps={this.props.socialIconProps}
      isFullWidth={this.shouldRenderAsFullWidth()}
    />;

  rightRailAd = () => (
    <RightRailAd adsState={this.props.sectionsProps.adsState}
      refreshEveryNSlides={AD_REFRESH_RATE_PER_SLIDE}
      shouldRefreshAdOnInterval={false}
      targetParentId={this.props.contentContainerId}
      slideshow
      slideIndex={this.props.slideIndex}
    />
  );

  slideDescription = ({ content }) =>
    <TextSection content={content.description} />;

  getAltTitle = ({ alt_title }) => {
    return alt_title || this.props.intl.formatMessage(i18nMessages.slide_number_alt_string, { slideNumber: this.props.slideIndex });
  };

  slideSection = ({ content, ...nthSlide }) => (
    <Section {...nthSlide}
      {...this.props.socialIconProps}
      {...pick(this.props.sectionsProps, [
        'adsState',
        'hide_product_info_button',
        'product_layout',
        'sold_content_type'
      ])}
      content={
        assign(
          omit(content, 'description'),
          { alt_title: this.getAltTitle(content) }
        )
      }
      isFullWidthSlideSection={this.props.isFullWidth}
      isDesktopSlideSection
    />
  );

  upNextComponent = () => (
    <UpNext {...this.props.upNext}
      isHovering={this.state.isHovering}
      shouldShowInitialAnimation={this.shouldShowInitialUpNextAnimation()}
      slideNumber={this.props.slideIndex}
    />
  );

  /*
   * The <Sections> component here is the description of the intro slide.
   * In renders of all the content in body.sections into the description.
   *   * Should not send SectionView Events. That's handled by *this*
   *     component, and triggered on slide change.
   *   * Should send Scroll Depth events.
   */
  renderBodySectionsAsIntroDescription = () => (
    <Sections {...this.props.sectionsProps}
      sections={this.props.bodySections}
      shouldSendGAViewEvents={false}
      shouldSendGAScrollDepthEvents
      relatedEntries={this.props.relatedEntries}
    />
  );

  handleBeginSlideshowButtonClick = () => {
    this.props.handleChangeSlide();
    sendAnalyticsEvent('Slideshow', 'Click', 'Begin Bottom Button');
    sendDataLayerEvent('Click', {
      actionType: 'Begin Slideshow',
      itemName: 'Slideshow',
      pageModule: 'Slideshow',
      slideshowComponent: 'Bottom Button'
    });
    scrollToTopDesktopAd();
  };

  hiddenNav = () => (
    <HiddenNav handleChangeSlide={this.props.handleChangeSlide}
      slideIndex={this.props.slideIndex}
      slideCount={this.props.slideshowSections.length}
    />
  );

  render() {
    const { handleChangeSlide, handleSeeAllSlidesClick, shouldDisplay,
      slideshowSections, sectionsProps, slideIndex, upNext, relatedEntries } = this.props;

    const isSlideFullWidth = this.shouldRenderAsFullWidth();
    const shouldDisplayUpNextComponent = this.shouldDisplayUpNextComponent();
    const nthSlide = slideshowSections[slideIndex - 1];

    const rightRailAd = isSlideFullWidth
      ? null
      : this.rightRailAd();

    const upNextComponent = shouldDisplayUpNextComponent
      ? this.upNextComponent()
      : null;

    const upNextUrl = shouldDisplayUpNextComponent
      ? upNext.url
      : null;

    const currentSlide = this.onIntroSlide()
      ? this.introSlide()
      : this.slideSection(nthSlide);

    const slideDescription = this.onIntroSlide()
      ? this.renderBodySectionsAsIntroDescription()
      : this.slideDescription(nthSlide);

    const secondaryBeginSlideshowButton = this.onIntroSlide()
      ? <BeginSlideshowButton handleClick={this.handleBeginSlideshowButtonClick} />
      : null;

    const shouldDisplayHiddenNav = !this.onIntroSlide()
      && isEmpty(nthSlide.content.clicktracker)
      && !isHTMLSection(nthSlide);

    const outerClassName = classNames('desktop-layout', {
      'hidden': !shouldDisplay
    });
    const innerClassName = isSlideFullWidth
      ? 'slide-full-width'
      : 'left-column';

    const relatedEntriesFallback = !isEmpty(relatedEntries)
      ? (<RelatedEntries relatedEntries={relatedEntries} onSlideshowArticle />)
      : null;

    const ad_sponsorship = get(sectionsProps.adsState, 'ad_data.ad_sponsorship', '');
    const nativeAd = (
      <NativeAd
        adsState={sectionsProps.adsState}
        uniqueId={'native-add'}
        position={'native'}
        className="native-ad"
        lazyLoadCushionPx={600}
        fallbackElement={relatedEntriesFallback}
      />
    );

    const nativeAdOrRelatedEntries = trim(ad_sponsorship).length === 0 ? nativeAd : null;

    return (
      <div className={outerClassName}>
        <div className={innerClassName}>
          <div className="slideshow"
            onMouseEnter={this.handleMouseEnter}
            onMouseLeave={this.handleMouseLeave}
          >
            <TrinityAudioPlayer />
            <NavBar location={this.context.location}
              handleChangeSlide={handleChangeSlide}
              handleSeeAllSlidesClick={handleSeeAllSlidesClick}
              slideIndex={slideIndex}
              slideCount={slideshowSections.length}
              upNextUrl={upNextUrl}
            />
            {shouldDisplayHiddenNav && this.hiddenNav()}
            {upNextComponent}
            {currentSlide}
          </div>
          <div id={EDITORIAL_CONTENT_NODE_ID}>
            {slideDescription}
            {secondaryBeginSlideshowButton}
          </div>
          <ProductDescriptions {...nthSlide} />
          {this.onIntroSlide() && nativeAdOrRelatedEntries}
        </div>
        {rightRailAd}
      </div>
    );
  }
}

DesktopLayout.propTypes = {
  bodySections: PropTypes.arrayOf(PropTypes.object).isRequired,
  handleChangeSlide: PropTypes.func.isRequired,
  handleSeeAllSlidesClick: PropTypes.func.isRequired,
  hasFullWidthIntroSlide: PropTypes.bool.isRequired,
  isFullWidth: PropTypes.bool.isRequired,
  openerImage: PropTypes.object,
  sectionsProps: PropTypes.shape({
    adsState: PropTypes.object.isRequired,
    product_layout: PropTypes.bool.isRequired
  }).isRequired,
  shouldDisplay: PropTypes.bool.isRequired,
  slideIndex: PropTypes.number.isRequired,
  slideshowSections: PropTypes.arrayOf(PropTypes.object).isRequired,
  socialIconProps: PropTypes.object.isRequired,
  contentContainerId: PropTypes.string.isRequired,
  upNext: PropTypes.object.isRequired,
  updateUpNext: PropTypes.func.isRequired,
  relatedEntries: PropTypes.array,
  intl: PropTypes.object.isRequired
};

DesktopLayout.contextTypes = {
  location: PropTypes.object
};

export default injectIntl(DesktopLayout);
